// src/screens/ProveedoresScreen.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebaseConfig';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore'; // Added orderBy
import { useAuth } from '../context/AuthContext';
import {
  IoArrowBack,
  IoQrCodeOutline,
  IoPencil,
  IoWaterOutline,
  IoFlashOutline,
  IoSparklesOutline,
  IoHammerOutline,
  IoBuildOutline,
  IoBrushOutline,
  IoFlameOutline,
  IoHappyOutline,
  IoPawOutline,
  IoCutOutline,
  IoConstructOutline,
  IoAdd,
  IoSearchOutline // Search icon not used but good to have
} from 'react-icons/io5';
import AccesoModal from '../components/AccesoModal';
import './ProveedoresScreen.css'; // --- Premium CSS ---
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';

// Helper for icons with cohesive colors
const getIconoServicio = (servicio) => {
  const s = (servicio || 'otro').toLowerCase();

  const iconMap = {
    'plomero': { icon: <IoWaterOutline />, bg: '#eff6ff', color: '#3b82f6' }, // Blue
    'electricista': { icon: <IoFlashOutline />, bg: '#fef3c7', color: '#f59e0b' }, // Amber
    'domésticos': { icon: <IoSparklesOutline />, bg: '#ecfdf5', color: '#10b981' }, // Emerald
    'carpintero': { icon: <IoHammerOutline />, bg: '#fff7ed', color: '#ea580c' }, // Orange
    'albañil': { icon: <IoBuildOutline />, bg: '#f3f4f6', color: '#6b7280' }, // Gray
    'pintor': { icon: <IoBrushOutline />, bg: '#f5f3ff', color: '#8b5cf6' }, // Violet
    'herrero': { icon: <IoFlameOutline />, bg: '#fef2f2', color: '#ef4444' }, // Red
    'niñera': { icon: <IoHappyOutline />, bg: '#fdf2f8', color: '#ec4899' }, // Pink
    'perros': { icon: <IoPawOutline />, bg: '#f0fdf4', color: '#22c55e' }, // Green
    'jardinero': { icon: <IoCutOutline />, bg: '#ecfccb', color: '#84cc16' }, // Lime
  };

  const defaultStyle = { icon: <IoConstructOutline />, bg: '#f8fafc', color: '#64748b' };

  // Find key that matches
  const key = Object.keys(iconMap).find(k => s.includes(k));
  const style = key ? iconMap[key] : defaultStyle;

  return (
    <div className="proveedor-icon-wrapper" style={{ backgroundColor: style.bg, color: style.color }}>
      {style.icon}
    </div>
  );
}

const formatFecha = (timestamp) => {
  if (!timestamp) return 'N/A';
  // Check if Active or Expired for badge logic? 
  // No, just return formatted date.
  return timestamp.toDate().toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' });
}

function ProveedoresScreen() {
  const navigate = useNavigate();
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAcceso, setSelectedAcceso] = useState(null);
  const { currentUser } = useAuth();

  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, "accesos"),
      where("residenteId", "==", currentUser.uid),
      where("tipo", "==", "Proveedor"),
      orderBy("expira", "desc") // Order by expiration date
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const listaProveedores = [];
      querySnapshot.forEach((doc) => {
        listaProveedores.push({ ...doc.data(), id: doc.id });
      });
      setProveedores(listaProveedores);
      setLoading(false);
    }, (error) => {
      console.error("Error al cargar proveedores: ", error); // Likely requires Index
      setLoading(false);
    });
    return () => unsubscribe();
  }, [currentUser]);

  const handleVerAcceso = (prov) => {
    setSelectedAcceso(prov);
  };

  return (
    <>
      <KondoBrandHeader showBack={false} />
      <div className="proveedores-container">
        {/* --- Clean Navbar --- */}
        {/* Sticky Compact Header */}
        <div className="proveedores-fixed-wrapper">
          <div className="proveedores-banner-compact">
            <button onClick={() => navigate('/accesos')} className="proveedores-back-btn glass">
              <IoArrowBack size={24} />
            </button>

            <div className="proveedores-banner-content">
              <h2>Mis Proveedores</h2>
              <p>Servicios y mantenimiento</p>
            </div>

            <button className="proveedores-action-btn glass" onClick={() => navigate('/accesos/proveedores/nuevo')}>
              <IoAdd size={24} />
            </button>

            <div className="proveedores-banner-icon">
              <IoBuildOutline />
            </div>
          </div>
        </div>

        <div>
          {loading && <p style={{ textAlign: 'center', color: '#64748b' }}>Cargando...</p>}

          {!loading && proveedores.length === 0 && (
            <div className="proveedores-empty">
              <IoSearchOutline />
              <h3>No tienes proveedores registrados</h3>
              <p>Agrega personal de servicio para generarles accesos QR.</p>
            </div>
          )}

          {/* --- GRID LAYOUT --- */}
          {!loading && proveedores.length > 0 && (
            <div className="proveedores-grid">
              {proveedores.map((prov) => {
                const isExpired = prov.expira && prov.expira.toDate() < new Date();

                return (
                  <div key={prov.id} className="proveedor-card">
                    {/* Top Row: Icon + Info */}
                    <div className="prove-header-row">
                      {getIconoServicio(prov.tipoServicio)}

                      <div className="prove-info-col">
                        <div className="proveedor-info">
                          <h3>{prov.nombreComercial || prov.nombreCompleto}</h3>
                          <div className="proveedor-service">
                            {prov.tipoServicio || 'Servicio'}
                            <span style={{ margin: '0 4px' }}>•</span>
                            <span style={{ fontSize: '0.75rem', color: isExpired ? '#dc2626' : '#059669' }}>
                              {isExpired ? 'Expirado' : 'Activo'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Meta Data */}
                    <div className="proveedor-meta">
                      <span>📞 {prov.contacto}</span>
                      <span>Vence: {formatFecha(prov.expira)}</span>
                    </div>

                    {/* Actions */}
                    <div className="proveedor-actions">
                      <button className="action-btn-main" onClick={() => navigate(`/accesos/proveedores/editar/${prov.id}`)}>
                        <IoPencil /> Editar
                      </button>
                      <button
                        className="action-btn-icon"
                        title="Ver QR"
                        onClick={() => handleVerAcceso(prov)}
                      >
                        <IoQrCodeOutline /> QR
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {selectedAcceso && (
          <AccesoModal
            acceso={selectedAcceso}
            onClose={() => setSelectedAcceso(null)}
          />
        )}
      </div>
      <KondoBottomNav activeRoute="/accesos" />
    </>
  );
}

export default ProveedoresScreen;